import { useState, useEffect } from 'react'
import { 
  Container, 
  useToast,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Box
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import DataTable from '../../../common/DataTable/DataTable'
import TaxonomyModel from '../../../../models/TaxonomyModel' 

const TaxonomyList = () => {
  const [taxonomies, setTaxonomies] = useState([])
  const [filteredTaxonomies, setFilteredTaxonomies] = useState([])
  const [loading, setLoading] = useState(true)
  const toast = useToast()
  const navigate = useNavigate()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Enhanced columns with better formatting
  const columns = [
    { 
      key: 'name', 
      label: 'Taxonomy Name',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">{value}</Text>
          <Text fontSize="xs" color="gray.500">ID: #{row.id}</Text>
        </VStack>
      )
    },
    { 
      key: 'description', 
      label: 'Description',
      render: (value) => (
        <Text fontSize="sm" maxW="300px" noOfLines={2}>
          {value || 'No description provided'}
        </Text>
      )
    },
    { 
      key: 'hierarchical', 
      label: 'Hierarchical',
      render: (value) => (
        <Badge colorScheme={value ? 'green' : 'gray'} variant="subtle">
          {value ? 'Yes' : 'No'}
        </Badge>
      )
    },
    { 
      key: 'updated_at', 
      label: 'Last Modified',
      render: (value) => (
        <Text fontSize="sm" color="gray.600">
          {value ? new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : 'N/A'}
        </Text>
      )
    }
  ]

  const fetchTaxonomies = async () => {
    try {
      const data = await TaxonomyModel.getAllTaxonomies();
      setTaxonomies(data)
      setFilteredTaxonomies(data)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch taxonomies',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTaxonomies()
  }, [])

  const handleSearch = (searchTerm) => {
    const lowercaseSearch = searchTerm.toLowerCase()
    const filtered = taxonomies.filter(taxonomy =>
      taxonomy.name.toLowerCase().includes(lowercaseSearch) ||
      (taxonomy.description && taxonomy.description.toLowerCase().includes(lowercaseSearch))
    )
    setFilteredTaxonomies(filtered)
  }

  const handleEdit = (taxonomy) => {
    navigate(`/taxonomies/edit/${taxonomy.id}`)
  }
  
  const handleDelete = async (taxonomy) => {
    try {
      await TaxonomyModel.deleteTaxonomy(taxonomy.id);

      toast({
        title: 'Success',
        description: 'Taxonomy deleted successfully',
        status: 'success',
        duration: 3000
      })

      fetchTaxonomies()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete taxonomy',
        status: 'error',
        duration: 3000
      })
    }
  }

  const handleCreate = () => {
    navigate(`/taxonomies/create`)
  }

  // Calculate statistics
  const getTaxonomyStats = () => {
    const total = taxonomies.length
    const hierarchical = taxonomies.filter(tax => tax.hierarchical).length
    const flat = taxonomies.filter(tax => !tax.hierarchical).length
    
    return { total, hierarchical, flat }
  }

  const stats = getTaxonomyStats()

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/content-types">Content Types</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Taxonomies</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <Heading size="lg" mb={2}>Taxonomies</Heading>
          <Text color="gray.500">
            Manage classification systems for your content. Create hierarchical or flat taxonomies.
          </Text>
        </Box>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Taxonomies</StatLabel>
                <StatNumber color="blue.500">{stats.total}</StatNumber>
                <StatHelpText>All taxonomies in your system</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Hierarchical</StatLabel>
                <StatNumber color="green.500">{stats.hierarchical}</StatNumber>
                <StatHelpText>Tree-structured taxonomies</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Flat</StatLabel>
                <StatNumber color="purple.500">{stats.flat}</StatNumber>
                <StatHelpText>Simple tag-based taxonomies</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Enhanced DataTable */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody p={0}>
            <DataTable
              columns={columns}
              data={filteredTaxonomies}
              onSearch={handleSearch}
              onEdit={handleEdit}
              onCreate={handleCreate}
              onDelete={handleDelete}
              searchPlaceholder="Search taxonomies by name or description..."
              isLoading={loading}
              title="Taxonomies Management"
              createButtonText="Create New Taxonomy"
              emptyStateTitle="No taxonomies found"
              emptyStateDescription={
                filteredTaxonomies.length === 0 && taxonomies.length > 0
                  ? "No taxonomies match your search criteria. Try adjusting your search terms."
                  : "Get started by creating your first taxonomy to classify your content."
              }
              loadingText="Loading taxonomies..."
            />
          </CardBody>
        </Card>
      </VStack>
    </Container>
  )
}

export default TaxonomyList
